<script lang="ts" setup>
import { computed } from 'vue';
import { useRoute } from 'vue-router';
import type { MenuItem as MenuItemClass } from '@/objects/MenuItem';
import { useAccessStore, useEventStoreRef } from '@/stores';
import { IconComp, QuickCreate } from '@/components';
import { useAccessValidator } from '@/composeables/accessValidator';
import MenuItem from './MenuItem.vue';

const props = defineProps({
    menu: Array<MenuItemClass>,
});

const { eventID } = useEventStoreRef();

const { HasFunctionality } = useAccessStore();

const route = useRoute();

const currentRouteWithoutId = computed(() => {
    if (route.params.id) {
        return route.path.replace('/' + route.params.id, '');
    }
    return route.path;
});

const size: 'BIG' | 'SMALL' = 'BIG';
</script>
<template>
    <div class="flex-column mb-auto p-3 overflow-y-auto">
        <ul class="nav flex-column mb-auto">
            <template
                v-for="mi in menu"
                :key="mi.key()"
            >
                <MenuItem
                    v-if="mi.subItems.length == 0"
                    :currentRouteWithoutId="currentRouteWithoutId"
                    :eventID="eventID"
                    :menuitem="mi"
                    :size="size"
                    :indent="false"
                />
                <template v-else>
                    <li
                        :class="['nav-item', mi.groupStart ? 'nav-item-group' : '']"
                        v-if="
                            mi.isVisible(size, eventID) &&
                            useAccessValidator().CheckMenuAccess(mi) &&
                            (mi.functionalityTypes ?? []).every((f) => HasFunctionality(f))
                        "
                    >
                        <a
                            :data-bs-target="'#sub' + mi.key()"
                            data-bs-toggle="collapse"
                            role="button"
                            class="mt-4 nav-header nav-link nav-link-subitems"
                            aria-current="page"
                        >
                            <span
                                class="menuitem-icon"
                                data-bs-toggle="tooltip"
                                data-bs-offset="0,30"
                                data-bs-placement="right"
                                :data-bs-title="$tm(mi.translationKey)"
                            >
                                <IconComp :icon="mi.icon" />
                            </span>
                            <span class="menuitem-text">{{ $tm(mi.translationKey) }}</span>
                        </a>
                        <ul
                            class="nav mb-auto collapse show"
                            :id="'sub' + mi.key()"
                        >
                            <template
                                v-for="imi in mi.subItems"
                                :key="imi.key()"
                            >
                                <MenuItem
                                    v-if="
                                        useAccessValidator().CheckMenuAccess(imi) &&
                                        (imi.functionalityTypes ?? []).every((f) => HasFunctionality(f))
                                    "
                                    :currentRouteWithoutId="currentRouteWithoutId"
                                    :eventID="eventID"
                                    :menuitem="imi"
                                    :size="size"
                                    :indent="true"
                                    class="d-block w-100"
                                />
                            </template>
                        </ul>
                    </li>
                </template>
            </template>
        </ul>
    </div>
</template>
